import { can } from '@kia/shared';
import { api } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, ErrorRetry, useToast } from '../ui';

function downloadText(name: string, text: string, mime = 'text/plain'): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
const stamp = () => new Date().toISOString().slice(0, 10);

/** Export/briefing panel — the analytical feed the AI assistant consumes; composed into the
 * merged «تحلیل و دستیار هوشمند» tab beneath the chat. Own card, no shared state. */
export function ExportPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user ? can(user.role, 'audit.read') : false;
  const { data, status, error, reload } = useApiResource<unknown>('/api/export.md');
  const preview =
    data == null ? '' : typeof data === 'string' ? data : JSON.stringify(data, null, 2);

  async function download(kind: 'md' | 'json' | 'backup' | 'errors') {
    const paths = {
      md: '/api/export.md',
      json: '/api/export.json',
      backup: '/api/backup.json',
      errors: '/api/errorlog',
    } as const;
    const r = await api(paths[kind]);
    if (!r.ok) return toast('خطا در دریافت', 'error');
    const text = typeof r.body === 'string' ? r.body : JSON.stringify(r.body, null, 2);
    const names = {
      md: `briefing_${stamp()}.md`,
      json: `export_${stamp()}.json`,
      backup: `backup_${stamp()}.json`,
      errors: `error_log_${stamp()}.json`,
    } as const;
    downloadText(names[kind], text, kind === 'md' ? 'text/markdown' : 'application/json');
  }

  async function copyBrief() {
    try {
      await navigator.clipboard.writeText(preview);
      toast('کپی شد — در چت هوش مصنوعی بچسبانید');
    } catch {
      toast('کپی نشد؛ از دکمهٔ دریافت استفاده کنید', 'error');
    }
  }

  return (
    <Card title="خروجی داده و گزارش تحلیلی">
      <p className="muted" style={{ marginTop: 0 }}>
        همان دادهٔ زنده که دستیار هوشمند بالا استفاده می‌کند — برای تحلیل بیرونی دریافت یا کپی کنید.
        همهٔ مبالغ ﷼.
      </p>
      <div className="row">
        <Button onClick={() => download('md')}>دریافت «گزارش تحلیلی» (متن)</Button>
        <Button variant="secondary" onClick={() => download('json')}>
          دادهٔ کامل (JSON)
        </Button>
        {isAdmin ? (
          <>
            <Button variant="secondary" onClick={() => download('backup')}>
              پشتیبان کامل (JSON)
            </Button>
            <Button variant="secondary" onClick={() => download('errors')}>
              گزارش خطاها
            </Button>
          </>
        ) : null}
        <Button variant="ghost" onClick={copyBrief}>
          کپی گزارش برای چسباندن در چت
        </Button>
      </div>
      <h3 style={{ color: 'var(--navy-l)', fontSize: 13, margin: '16px 0 6px' }}>
        پیش‌نمایش گزارش
      </h3>
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            background: '#f6f9fd',
            border: '1px solid #dbe6f1',
            borderRadius: 9,
            padding: 14,
            fontSize: 12,
            lineHeight: 1.7,
            maxHeight: 380,
            overflow: 'auto',
            direction: 'ltr',
            textAlign: 'left',
          }}
        >
          {preview}
        </pre>
      )}
    </Card>
  );
}
